/* =========================================================
   REVAINE UNIVERSAL SIZE FINDER
   File: assets/revaine-size-finder.js

   Supports:
   - Height / weight sliders and manual input
   - cm / ft-in, kg / lb and chest units
   - Category-specific sizing profiles
   - Optional product measurement data
   - Size recommendation and Shopify variant selection
   - Modal accessibility and keyboard controls

   Measurement objects use centimetres unless specified.
   ========================================================= */

(() => {
  'use strict';

  if (window.__revaineSizeFinderLoaded) return;
  window.__revaineSizeFinderLoaded = true;

  const SIZE_ORDER = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

  /*
   * Preliminary Bangladesh-oriented estimates.
   * These are NOT validated garment size charts.
   * Replace them with brand-specific data as available.
   *
   * Weight bands overlap intentionally.
   */
  const CATEGORY_PROFILES = {
    jacket: {
      label: 'Jacket',
      sizes: ['M', 'L', 'XL', 'XXL', 'XXXL'],
      bands: [
        { size: 'M', minWeight: 45, maxWeight: 65, minHeight: 155, maxHeight: 173 },
        { size: 'L', minWeight: 58, maxWeight: 76, minHeight: 160, maxHeight: 178 },
        { size: 'XL', minWeight: 68, maxWeight: 88, minHeight: 165, maxHeight: 183 },
        { size: 'XXL', minWeight: 80, maxWeight: 102, minHeight: 168, maxHeight: 188 },
        { size: 'XXXL', minWeight: 94, maxWeight: 120, minHeight: 170, maxHeight: 195 }
      ]
    },

    tshirt: {
      label: 'T-shirt',
      sizes: ['M', 'L', 'XL', 'XXL', 'XXXL'],
      bands: [
        { size: 'M', minWeight: 45, maxWeight: 65, minHeight: 155, maxHeight: 173 },
        { size: 'L', minWeight: 58, maxWeight: 76, minHeight: 160, maxHeight: 178 },
        { size: 'XL', minWeight: 68, maxWeight: 88, minHeight: 165, maxHeight: 183 },
        { size: 'XXL', minWeight: 80, maxWeight: 102, minHeight: 168, maxHeight: 188 },
        { size: 'XXXL', minWeight: 94, maxWeight: 120, minHeight: 170, maxHeight: 195 }
      ]
    },

    hoodie: {
      label: 'Hoodie',
      sizes: ['M', 'L', 'XL', 'XXL', 'XXXL'],
      bands: [
        { size: 'M', minWeight: 45, maxWeight: 65, minHeight: 155, maxHeight: 173 },
        { size: 'L', minWeight: 58, maxWeight: 76, minHeight: 160, maxHeight: 178 },
        { size: 'XL', minWeight: 68, maxWeight: 88, minHeight: 165, maxHeight: 183 },
        { size: 'XXL', minWeight: 80, maxWeight: 102, minHeight: 168, maxHeight: 188 },
        { size: 'XXXL', minWeight: 94, maxWeight: 120, minHeight: 170, maxHeight: 195 }
      ]
    },

    joggers: {
      label: 'Joggers / Pants',
      sizes: ['M', 'L', 'XL', 'XXL', 'XXXL'],
      bands: [
        { size: 'M', minWeight: 45, maxWeight: 65, minHeight: 155, maxHeight: 173 },
        { size: 'L', minWeight: 58, maxWeight: 76, minHeight: 160, maxHeight: 178 },
        { size: 'XL', minWeight: 68, maxWeight: 88, minHeight: 165, maxHeight: 183 },
        { size: 'XXL', minWeight: 80, maxWeight: 102, minHeight: 168, maxHeight: 188 },
        { size: 'XXXL', minWeight: 94, maxWeight: 120, minHeight: 170, maxHeight: 195 }
      ]
    },

    compression: {
      label: 'Compression Wear',
      sizes: ['M', 'L', 'XL', 'XXL', 'XXXL'],
      bands: [
        { size: 'M', minWeight: 45, maxWeight: 60, minHeight: 155, maxHeight: 173 },
        { size: 'L', minWeight: 55, maxWeight: 70, minHeight: 160, maxHeight: 178 },
        { size: 'XL', minWeight: 65, maxWeight: 82, minHeight: 165, maxHeight: 183 },
        { size: 'XXL', minWeight: 77, maxWeight: 96, minHeight: 168, maxHeight: 188 },
        { size: 'XXXL', minWeight: 90, maxWeight: 115, minHeight: 170, maxHeight: 195 }
      ]
    },

    general: {
      label: 'General',
      sizes: ['M', 'L', 'XL', 'XXL', 'XXXL'],
      bands: [
        { size: 'M', minWeight: 45, maxWeight: 65, minHeight: 155, maxHeight: 173 },
        { size: 'L', minWeight: 58, maxWeight: 76, minHeight: 160, maxHeight: 178 },
        { size: 'XL', minWeight: 68, maxWeight: 88, minHeight: 165, maxHeight: 183 },
        { size: 'XXL', minWeight: 80, maxWeight: 102, minHeight: 168, maxHeight: 188 },
        { size: 'XXXL', minWeight: 94, maxWeight: 120, minHeight: 170, maxHeight: 195 }
      ]
    }
  };

  const $ = (root, selector) => root.querySelector(selector);

  const $$ = (root, selector) =>
    Array.from(root.querySelectorAll(selector));

  const clamp = (value, min, max) =>
    Math.min(max, Math.max(min, value));

  const numberOrNull = value => {
    if (value === '' || value == null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const normalize = value =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');

  function detectCategory(root) {
    const explicit = normalize(root.dataset.profile);

    const aliases = [
      ['compression', ['compression', 'compressionshirt', 'compressionwear']],
      ['joggers', ['jogger', 'joggers', 'pants', 'trousers', 'bottoms']],
      ['jacket', ['jacket', 'jackets', 'racingjacket', 'outerwear', 'coat']],
      ['hoodie', ['hoodie', 'hoodies', 'sweatshirt']],
      ['tshirt', ['tshirt', 'tee', 'tees', 'tshirts', 'dropshoulder']]
    ];

    for (const [category, terms] of aliases) {
      if (terms.some(term => explicit.includes(term))) return category;
    }

    const productType = normalize(root.dataset.productType);

    for (const [category, terms] of aliases) {
      if (terms.some(term => productType.includes(term))) return category;
    }

    const title = normalize(root.dataset.productTitle);

    for (const [category, terms] of aliases) {
      if (terms.some(term => title.includes(term))) return category;
    }

    return 'general';
  }

  function parseMeasurements(root) {
    const raw = root.dataset.measurements;

    if (!raw) return null;

    try {
      const data = JSON.parse(raw);

      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return null;
      }

      return data;
    } catch (error) {
      console.warn('[ReVaine Size Finder] Invalid measurement JSON.', error);
      return null;
    }
  }

  /*
   * Expected measurement format:
   *
   * {
   *   "unit": "cm",
   *   "M":    { "chest": 104, "shoulder": 44, "length": 68 },
   *   "L":    { "chest": 110, "shoulder": 46, "length": 70 },
   *   "XL":   { "chest": 116, "shoulder": 48, "length": 72 },
   *   "XXL":  { "chest": 122, "shoulder": 50, "length": 74 },
   *   "XXXL": { "chest": 128, "shoulder": 52, "length": 76 }
   * }
   *
   * Example numbers are illustrative only. Do not use as
   * real ReVaine garment measurements without verifying them.
   */

  function getMeasurementForSize(measurements, size) {
    if (!measurements) return null;

    const key = Object.keys(measurements).find(
      item => normalize(item) === normalize(size)
    );

    if (!key || !measurements[key] || typeof measurements[key] !== 'object') {
      return null;
    }

    return measurements[key];
  }

  function getChestCm(measurements, size) {
    const entry = getMeasurementForSize(measurements, size);
    if (!entry) return null;

    const chest = numberOrNull(entry.chest);
    if (chest == null) return null;

    const unit = normalize(measurements.unit || entry.unit || 'cm');

    return unit === 'in' || unit === 'inch' || unit === 'inches'
      ? chest * 2.54
      : chest;
  }

  function getAvailableSizes(root, profile) {
    const form = findProductForm(root);
    const variants = form
      ? Array.from(form.querySelectorAll('select[name="id"] option'))
      : [];

    /*
     * Shopify's variant-ID select doesn't always expose sizes.
     * If it does not, use the category profile and measurement data.
     */
    const sizeCandidates = new Set(profile.sizes || SIZE_ORDER);

    const measurements = parseMeasurements(root);

    if (measurements) {
      Object.keys(measurements).forEach(key => {
        if (SIZE_ORDER.includes(key.toUpperCase())) {
          sizeCandidates.add(key.toUpperCase());
        }
      });
    }

    if (variants.length) {
      const optionLabels = variants.map(option => option.textContent.trim());

      const detected = optionLabels
        .map(label => {
          const match = label.match(/\b(XXXL|XXL|XL|L|M|S)\b/i);
          return match ? match[1].toUpperCase() : null;
        })
        .filter(Boolean);

      if (detected.length) {
        return Array.from(new Set(detected)).sort(
          (a, b) => SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b)
        );
      }
    }

    return Array.from(sizeCandidates).sort(
      (a, b) => SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b)
    );
  }

  function findProductForm(root) {
    const section = root.closest('.shopify-section');

    const candidates = [
      section && section.querySelector('product-form form'),
      section && section.querySelector('form[action*="/cart/add"]'),
      document.querySelector('product-form form'),
      document.querySelector('form[action*="/cart/add"]')
    ];

    return candidates.find(Boolean) || null;
  }

  function getVariantSelect(form) {
    if (!form) return null;
    return form.querySelector('select[name="id"]');
  }

  async function fetchProductData() {
    const path = window.location.pathname.replace(/\/$/, '');

    try {
      const response = await fetch(`${path}.js`, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return null;

      return await response.json();
    } catch (error) {
      console.warn('[ReVaine Size Finder] Product data unavailable.', error);
      return null;
    }
  }

function findSizeOption(form) {
  if (!form) return null;

  // ReVaine custom product option groups
  const customGroups = $$(form, '.rv-option[data-option-index]');

  const customSizeGroup = customGroups.find(group => {
    const label = $(group, '.rv-option__label');
    const labelText = normalize(label ? label.textContent : '');

    return (
      labelText.includes('size') ||
      labelText.includes('sizing')
    );
  });

  if (customSizeGroup) {
    const firstInput = $(
      customSizeGroup,
      '[data-option-input]'
    );

    if (firstInput) {
      return {
        type: 'radio',
        element: firstInput
      };
    }
  }

  // Standard Shopify size dropdowns
  const selects = $$(
    form,
    'select[name^="options["], select[name*="Size"], select[data-option-name]'
  );

  const sizeSelect = selects.find(select => {
    const name = normalize(select.name);
    const optionName = normalize(select.dataset.optionName);

    return (
      name.includes('size') ||
      optionName === 'size' ||
      Array.from(select.options).some(option =>
        SIZE_ORDER.includes(normalize(option.value).toUpperCase())
      )
    );
  });

  if (sizeSelect) {
    return {
      type: 'select',
      element: sizeSelect
    };
  }

  // Standard Shopify radio options
  const radios = $$(
    form,
    'input[type="radio"][name^="options["], input[type="radio"][name*="Size"]'
  );

  const radio = radios.find(input => {
    const name = normalize(input.name);

    return (
      name.includes('size') ||
      SIZE_ORDER.includes(normalize(input.value).toUpperCase())
    );
  });

  if (radio) {
    return {
      type: 'radio',
      element: radio
    };
  }

  return null;
}

  async function selectShopifySize(root, size) {
    const form = findProductForm(root);

    if (!form) {
      return {
        success: false,
        message: 'Could not find the product form. Please select your size manually.'
      };
    }

    const sizeOption = findSizeOption(form);

    if (sizeOption) {
      if (sizeOption.type === 'select') {
        const matchingOption = Array.from(sizeOption.element.options).find(
          option => normalize(option.value) === normalize(size)
        );

        if (matchingOption) {
          sizeOption.element.value = matchingOption.value;
          sizeOption.element.dispatchEvent(new Event('change', { bubbles: true }));

          return { success: true };
        }
      } else {
        const name = sizeOption.element.name;

        const matchingRadio = $$(
          form,
          `input[type="radio"][name="${CSS.escape(name)}"]`
        ).find(input => normalize(input.value) === normalize(size));

        if (matchingRadio) {
          matchingRadio.checked = true;
          matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));

          return { success: true };
        }
      }
    }

    /*
     * Fallback: fetch the product JSON and select the matching variant.
     * This requires an available variant with the requested size.
     */
    const product = await fetchProductData();

    if (!product || !Array.isArray(product.variants)) {
      return {
        success: false,
        message: 'Please select your size manually from the product page.'
      };
    }

    const variant = product.variants.find(item =>
      Array.isArray(item.options) &&
      item.options.some(option => normalize(option) === normalize(size)) &&
      item.available
    );

    if (!variant) {
      return {
        success: false,
        message: `An available ${size} variant could not be found. Please select your size manually.`
      };
    }

    const variantSelect = getVariantSelect(form);

    if (variantSelect) {
      variantSelect.value = String(variant.id);
      variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      let idInput = form.querySelector('input[name="id"]');

      if (!idInput) {
        idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = 'id';
        form.appendChild(idInput);
      }

      idInput.value = String(variant.id);
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return { success: true };
  }

  function chooseRecommendation({
    profile,
    measurements,
    heightCm,
    weightKg,
    chestCm,
    availableSizes
  }) {
    const sizes = availableSizes.filter(size =>
      (profile.sizes || SIZE_ORDER).includes(size)
    );

    if (!sizes.length) {
      return {
        size: null,
        confidence: 'low',
        message: 'No supported sizes were detected for this product.'
      };
    }

    /*
     * When a real chest chart exists, compare the customer's chest
     * with garment chest measurements. Garment chest is generally
     * larger than body chest; use an initial ease allowance.
     *
     * This is a starting rule, not a universal clothing standard.
     * The allowance should be calibrated per product category.
     */
    if (chestCm != null && measurements) {
      const hasChart = sizes.some(size => getChestCm(measurements, size) != null);

      if (hasChart) {
        const easeCm = Number.isFinite(Number(measurements.ease_cm))
          ? Number(measurements.ease_cm)
          : 8;

        const targetChest = chestCm + easeCm;

        const suitable = sizes.find(size => {
          const garmentChest = getChestCm(measurements, size);
          return garmentChest != null && garmentChest >= targetChest;
        });

        if (suitable) {
          return {
            size: suitable,
            confidence: 'measurement',
            message: 'Suggested using your chest measurement and this product’s garment chart.'
          };
        }

        return {
          size: sizes[sizes.length - 1],
          confidence: 'measurement',
          message: 'The largest available size may still be too small. Check the garment chart before ordering.'
        };
      }
    }

    const bands = (profile.bands || []).filter(band =>
      sizes.includes(band.size)
    );

    if (!bands.length) {
      return {
        size: sizes[0],
        confidence: 'low',
        message: 'No sizing matrix is configured. Please check the product measurements.'
      };
    }

    const candidates = bands
      .map(band => {
        const weightCenter = (band.minWeight + band.maxWeight) / 2;
        const heightCenter = (band.minHeight + band.maxHeight) / 2;

        const weightSpan = Math.max(1, band.maxWeight - band.minWeight);
        const heightSpan = Math.max(1, band.maxHeight - band.minHeight);

        const weightScore = Math.abs(weightKg - weightCenter) / weightSpan;
        const heightScore = Math.abs(heightCm - heightCenter) / heightSpan;

        const insideWeight =
          weightKg >= band.minWeight && weightKg <= band.maxWeight;

        const insideHeight =
          heightCm >= band.minHeight && heightCm <= band.maxHeight;

        return {
          size: band.size,
          score: weightScore * 0.72 + heightScore * 0.28,
          insideWeight,
          insideHeight
        };
      })
      .sort((a, b) => a.score - b.score);

    const best = candidates[0];

    const inRange = candidates.filter(
      candidate => candidate.insideWeight && candidate.insideHeight
    );

    const ambiguous = inRange.length > 1;

    return {
      size: best.size,
      confidence: ambiguous ? 'low' : 'estimate',
      message: ambiguous
        ? `Your measurements overlap multiple sizes. ${best.size} is an initial estimate; check this product’s chart before ordering.`
        : 'This is an initial height-weight estimate, not a guaranteed fit. Compare it with the product measurements.'
    };
  }

  function init(root) {
    if (root.dataset.rvsfInitialized === 'true') return;
    root.dataset.rvsfInitialized = 'true';

    const profileKey = detectCategory(root);
    const profile = CATEGORY_PROFILES[profileKey] || CATEGORY_PROFILES.general;
    const measurements = parseMeasurements(root);

    const categoryLabel = $(root, '[data-rvsf-category]');
    if (categoryLabel) categoryLabel.textContent = profile.label;

    const heightRange = $(root, '[data-rvsf-height-range]');
    const heightNumber = $(root, '[data-rvsf-height-number]');
    const heightOutput = $(root, '[data-rvsf-height-output]');

    const weightRange = $(root, '[data-rvsf-weight-range]');
    const weightNumber = $(root, '[data-rvsf-weight-number]');
    const weightOutput = $(root, '[data-rvsf-weight-output]');

    const chestInput = $(root, '[data-rvsf-chest]');
    const bodyFigure = $(root, '[data-rvsf-body]');
    const error = $(root, '[data-rvsf-error]');
    const progress = $(root, '[data-rvsf-progress]');

    let heightUnit = 'cm';
    let weightUnit = 'kg';
    let chestUnit = 'cm';
    let lastFocusedElement = null;
    let recommendedSize = null;
    let chestValueCm = null;

    const getHeightCm = () => Number(heightRange.value);
    const getWeightKg = () => Number(weightRange.value);

    function showError(message) {
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    function clearError() {
      if (!error) return;
      error.textContent = '';
      error.hidden = true;
    }

    function renderHeight() {
      const cm = getHeightCm();

      if (heightUnit === 'cm') {
        heightOutput.textContent = `${cm} cm`;
        heightNumber.value = String(cm);
        heightNumber.setAttribute('aria-label', 'Height in centimetres');
        heightNumber.min = '140';
        heightNumber.max = '205';
      } else {
        const totalInches = cm / 2.54;
        const feet = Math.floor(totalInches / 12);
        const inches = Math.round(totalInches - feet * 12);

        const normalizedFeet = inches === 12 ? feet + 1 : feet;
        const normalizedInches = inches === 12 ? 0 : inches;

        heightOutput.textContent = `${normalizedFeet} ft ${normalizedInches} in`;

        // The number field uses total inches when ft/in is selected.
        heightNumber.value = String(Math.round(cm / 2.54));
        heightNumber.setAttribute('aria-label', 'Height in total inches');
        heightNumber.min = '55';
        heightNumber.max = '81';
      }

      if (bodyFigure) {
        const scaleY = clamp(cm / 170, 0.86, 1.12);
        bodyFigure.style.transform = `scaleY(${scaleY})`;
        bodyFigure.style.transformOrigin = 'center top';
      }
    }

    function renderWeight() {
      const kg = getWeightKg();

      weightOutput.textContent = weightUnit === 'kg'
        ? `${kg} kg`
        : `${Math.round(kg * 2.2046226218)} lb`;

      weightNumber.value = weightUnit === 'kg'
        ? String(kg)
        : String(Math.round(kg * 2.2046226218));

      weightNumber.setAttribute(
        'aria-label',
        weightUnit === 'kg' ? 'Weight in kilograms' : 'Weight in pounds'
      );

      weightNumber.min = weightUnit === 'kg' ? '35' : '77';
      weightNumber.max = weightUnit === 'kg' ? '150' : '331';

      if (bodyFigure) {
        const widthScale = clamp(0.85 + (kg - 50) / 180, 0.82, 1.35);
        bodyFigure.style.transform =
          `scale(${widthScale}, ${clamp(getHeightCm() / 170, 0.86, 1.12)})`;
        bodyFigure.style.transformOrigin = 'center top';
      }
    }

    function setHeightFromNumber() {
      const raw = numberOrNull(heightNumber.value);
      if (raw == null) return;

      const cm = heightUnit === 'cm' ? raw : raw * 2.54;
      heightRange.value = String(Math.round(clamp(cm, 140, 205)));
      renderHeight();
      renderWeight();
    }

    function setWeightFromNumber() {
      const raw = numberOrNull(weightNumber.value);
      if (raw == null) return;

      const kg = weightUnit === 'kg' ? raw : raw / 2.2046226218;
      weightRange.value = String(Math.round(clamp(kg, 35, 150)));
      renderWeight();
    }

    function updateChestUnit(nextUnit) {
      if (nextUnit === chestUnit) return;

      const existing = numberOrNull(chestInput.value);

      if (existing != null) {
        const cm = chestUnit === 'in' ? existing * 2.54 : existing;
        const converted = nextUnit === 'in' ? cm / 2.54 : cm;

        chestInput.value = converted.toFixed(1).replace(/\.0$/, '');
      }

      chestUnit = nextUnit;

      $$(
        root,
        '[data-rvsf-chest-unit]'
      ).forEach(button => {
        button.classList.toggle(
          'is-active',
          button.dataset.rvsfChestUnit === nextUnit
        );
      });

      chestInput.placeholder = nextUnit === 'cm' ? 'Chest (cm)' : 'Chest (in)';
      chestInput.setAttribute(
        'aria-label',
        nextUnit === 'cm'
          ? 'Chest measurement in centimetres'
          : 'Chest measurement in inches'
      );
    }

    function updateHeightUnit(nextUnit) {
      heightUnit = nextUnit;

      $$(
        root,
        '[data-rvsf-height-unit]'
      ).forEach(button => {
        button.classList.toggle(
          'is-active',
          button.dataset.rvsfHeightUnit === nextUnit
        );
      });

      renderHeight();
    }

    function updateWeightUnit(nextUnit) {
      weightUnit = nextUnit;

      $$(
        root,
        '[data-rvsf-weight-unit]'
      ).forEach(button => {
        button.classList.toggle(
          'is-active',
          button.dataset.rvsfWeightUnit === nextUnit
        );
      });

      renderWeight();
    }

    function openModal() {
      lastFocusedElement = document.activeElement;
      root.hidden = false;
      document.body.style.overflow = 'hidden';

      const dialog = $(root, '.rvsf__dialog');
      if (dialog) dialog.focus();

      clearError();
    }

    function closeModal() {
      root.hidden = true;
      document.body.style.overflow = '';

      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
      }
    }

    function setStep(step) {
      $$(
        root,
        '[data-rvsf-step]'
      ).forEach(panel => {
        panel.hidden = Number(panel.dataset.rvsfStep) !== step;
      });

      if (progress) {
        progress.style.width = step === 1 ? '50%' : '100%';
      }

      clearError();
    }

    function validateMeasurements() {
      const height = getHeightCm();
      const weight = getWeightKg();

      if (!Number.isFinite(height) || height < 140 || height > 205) {
        showError('Please enter a height between 140 and 205 cm.');
        return false;
      }

      if (!Number.isFinite(weight) || weight < 35 || weight > 150) {
        showError('Please enter a weight between 35 and 150 kg.');
        return false;
      }

      if (chestInput.value.trim() !== '') {
        const chestValue = numberOrNull(chestInput.value);

        if (chestValue == null || chestValue <= 0) {
          showError('Please enter a valid chest measurement or leave it blank.');
          return false;
        }

        chestValueCm = chestUnit === 'in'
          ? chestValue * 2.54
          : chestValue;

        if (chestValueCm < 60 || chestValueCm > 160) {
          showError('Please check your chest measurement. It should be between 60 and 160 cm.');
          return false;
        }
      } else {
        chestValueCm = null;
      }

      clearError();
      return true;
    }

    async function calculate() {
      if (!validateMeasurements()) return;

      const availableSizes = getAvailableSizes(root, profile);

      const result = chooseRecommendation({
        profile,
        measurements,
        heightCm: getHeightCm(),
        weightKg: getWeightKg(),
        chestCm: chestValueCm,
        availableSizes
      });

      if (!result.size) {
        showError(result.message || 'Unable to calculate a size for this product.');
        return;
      }

      recommendedSize = result.size;

      const resultSize = $(root, '[data-rvsf-result-size]');
      const resultCopy = $(root, '[data-rvsf-result-copy]');
      const notice = $(root, '[data-rvsf-notice]');

      if (resultSize) resultSize.textContent = result.size;
      if (resultCopy) resultCopy.textContent = result.message;

      if (notice) {
        notice.textContent = result.confidence === 'measurement'
          ? 'Based on the available garment chest measurement. Check length, shoulder and sleeve measurements too.'
          : 'Height and weight alone cannot guarantee fit. Check the actual garment measurements before ordering.';
      }

      setStep(2);
    }

    root.addEventListener('click', async event => {
      const closeButton = event.target.closest('[data-rvsf-close]');

      if (closeButton && root.contains(closeButton)) {
        closeModal();
        return;
      }

      const heightUnitButton = event.target.closest('[data-rvsf-height-unit]');
      if (heightUnitButton && root.contains(heightUnitButton)) {
        updateHeightUnit(heightUnitButton.dataset.rvsfHeightUnit);
        return;
      }

      const weightUnitButton = event.target.closest('[data-rvsf-weight-unit]');
      if (weightUnitButton && root.contains(weightUnitButton)) {
        updateWeightUnit(weightUnitButton.dataset.rvsfWeightUnit);
        return;
      }

      const chestUnitButton = event.target.closest('[data-rvsf-chest-unit]');
      if (chestUnitButton && root.contains(chestUnitButton)) {
        updateChestUnit(chestUnitButton.dataset.rvsfChestUnit);
        return;
      }

      if (event.target.closest('[data-rvsf-continue]')) {
        await calculate();
        return;
      }

      if (event.target.closest('[data-rvsf-back]')) {
        setStep(1);
        return;
      }

      if (event.target.closest('[data-rvsf-select]')) {
        if (!recommendedSize) {
          showError('Please calculate your size first.');
          return;
        }

        const button = event.target.closest('[data-rvsf-select]');
        const originalText = button.textContent;

        button.disabled = true;
        button.textContent = 'Selecting…';

        try {
          const result = await selectShopifySize(root, recommendedSize);

          if (result.success) {
            closeModal();
          } else {
            showError(result.message);
          }
        } catch (error) {
          console.error('[ReVaine Size Finder] Variant selection failed.', error);
          showError('Could not select this variant automatically. Please select your size manually.');
        } finally {
          button.disabled = false;
          button.textContent = originalText;
        }
      }
    });

    heightRange.addEventListener('input', () => {
      renderHeight();
      renderWeight();
    });

    heightNumber.addEventListener('change', setHeightFromNumber);
    heightNumber.addEventListener('keydown', event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        setHeightFromNumber();
      }
    });

    weightRange.addEventListener('input', renderWeight);
    weightNumber.addEventListener('change', setWeightFromNumber);
    weightNumber.addEventListener('keydown', event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        setWeightFromNumber();
      }
    });

    chestInput.addEventListener('input', () => {
      clearError();
    });

    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-rvsf-open]');

      if (!trigger) return;

      const targetId = trigger.getAttribute('data-rvsf-open');

      if (targetId && targetId !== root.id) return;

      openModal();
    });

    document.addEventListener('keydown', event => {
      if (root.hidden) return;

      if (event.key === 'Escape') {
        closeModal();
        return;
      }

      if (event.key === 'Tab') {
        const dialog = $(root, '.rvsf__dialog');
        if (!dialog) return;

        const focusable = $$(
          dialog,
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]'
        ).filter(element => !element.closest('[hidden]'));

        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    renderHeight();
    renderWeight();
    updateChestUnit('cm');
    setStep(1);
  }

  function initializeAll() {
    document.querySelectorAll('[data-rvsf-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll);
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', initializeAll);
})();
// ReVaine Size Finder — reliable open/close handling
document.addEventListener('click', function (event) {
  var openButton = event.target.closest('[data-rv-size-finder-open]');
  var closeButton = event.target.closest('[data-rvsf-close]');

  if (openButton) {
    event.preventDefault();

    var root = document.querySelector('[data-rvsf-root]');
    if (!root) {
      console.error('ReVaine Size Finder: modal root not found.');
      return;
    }

    root.hidden = false;
    root.classList.add('is-open');
    document.documentElement.classList.add('rvsf-lock');

    var dialog = root.querySelector('.rvsf__dialog');
    if (dialog) dialog.focus();
  }

  if (closeButton) {
    var modal = closeButton.closest('[data-rvsf-root]');
    if (modal) {
      modal.hidden = true;
      modal.classList.remove('is-open');
      document.documentElement.classList.remove('rvsf-lock');
    }
  }
});